import { Hash, Mic, PhoneForwarded, PhoneOff } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { TranscriptTurnTimestamp } from '@apps/web/components/voice_calls/call_transcript_display_utils';
import {
    getDisconnectAttributionLabel,
    isPhoebeDisconnectAttribution,
    type VoiceCallDisconnectAttributionLike,
} from '@apps/web/components/voice_calls/voice_call_disconnect_attribution';
import { type VoiceCallTranscriptViewModel } from '@apps/web/components/voice_calls/voice_call_transcript_card';
import { cn } from '@apps/web/lib/utils';

// Speaker labels that represent the agency side of the call: Phoebe's voice
// agent, and the back-office "Representative" label that formatSotSpeaker
// emits for post-transfer agent turns. Everything else is the other party on
// the call and is normalized to "Caller" in the UI.
const AGENT_SPEAKER_PATTERN = /^(agent|phoebe|assistant|representative)$/i;
// Post-transfer turns are handled by a human back-office representative, not
// Phoebe, so they keep a distinct label.
const REPRESENTATIVE_SPEAKER_PATTERN = /^representative$/i;

function getSpeakerDisplayLabel(speakerLabel: string): string {
    const normalized = speakerLabel.trim();
    if (!AGENT_SPEAKER_PATTERN.test(normalized)) {
        return 'Caller';
    }
    return REPRESENTATIVE_SPEAKER_PATTERN.test(normalized)
        ? 'Representative'
        : 'Phoebe';
}

// Parse a "M:SS" / "MM:SS" / "H:MM:SS" timestamp label (optionally prefixed
// with "~" for approximate offsets) into seconds, so a turn can seek the
// recording. Returns null when the label is missing or unparsable.
export function parseTimestampLabelToSeconds(
    label: string | null,
): number | null {
    if (!label) {
        return null;
    }
    const parts = label.replace('~', '').trim().split(':');
    if (parts.length < 2 || parts.length > 3) {
        return null;
    }
    const numbers = parts.map((part) => Number(part));
    if (numbers.some((value) => !Number.isFinite(value))) {
        return null;
    }
    return numbers.reduce((total, value) => total * 60 + value, 0);
}

function SectionHeader({
    Icon,
    label,
}: {
    readonly Icon: typeof Mic;
    readonly label: string;
}) {
    return (
        <div className="text-muted-foreground flex items-center gap-2 text-xs font-semibold tracking-wide uppercase">
            <Icon className="h-4 w-4" />
            <span>{label}</span>
        </div>
    );
}

function CallEndedTimelineEvent({
    attribution,
    label,
}: {
    readonly attribution: VoiceCallDisconnectAttributionLike;
    readonly label: string;
}) {
    const isPhoebeDisconnect = isPhoebeDisconnectAttribution(attribution);

    return (
        <div
            aria-label={`Call ended: ${label}`}
            className={cn(
                'flex items-start gap-3 rounded-md border px-3 py-2',
                isPhoebeDisconnect
                    ? 'border-red-200 bg-red-50/70 dark:border-red-900/60 dark:bg-red-950/30'
                    : 'border-slate-200 bg-slate-50/80 dark:border-slate-800/70 dark:bg-slate-950/30',
            )}
        >
            <PhoneOff
                className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    isPhoebeDisconnect
                        ? 'text-red-700 dark:text-red-200'
                        : 'text-slate-600 dark:text-slate-300',
                )}
            />
            <div className="min-w-0">
                <p
                    className={cn(
                        'text-xs font-semibold tracking-wide uppercase',
                        isPhoebeDisconnect
                            ? 'text-red-800 dark:text-red-100'
                            : 'text-slate-700 dark:text-slate-200',
                    )}
                >
                    Call ended
                </p>
                <p
                    className={cn(
                        'text-sm font-medium',
                        isPhoebeDisconnect
                            ? 'text-red-700 dark:text-red-300'
                            : 'text-slate-950 dark:text-slate-50',
                    )}
                >
                    {label}
                </p>
            </div>
        </div>
    );
}

function AttemptedTransferTimelineEvent({
    destinationLabel,
}: {
    readonly destinationLabel: string;
}) {
    return (
        <div
            aria-label={`Transfer attempted: ${destinationLabel}`}
            className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2 dark:border-slate-800/70 dark:bg-slate-950/30"
        >
            <PhoneForwarded className="mt-0.5 h-4 w-4 shrink-0 text-slate-600 dark:text-slate-300" />
            <div className="min-w-0">
                <p className="text-xs font-semibold tracking-wide text-slate-700 uppercase dark:text-slate-200">
                    Transfer attempted
                </p>
                <p className="text-sm font-medium text-slate-950 dark:text-slate-50">
                    {`${destinationLabel} — didn’t connect`}
                </p>
            </div>
        </div>
    );
}

export function VoiceCallsTranscript({
    viewModel,
    disconnectAttribution,
    onSeek,
    afterSummary,
    showSummary = true,
}: {
    readonly viewModel: VoiceCallTranscriptViewModel;
    readonly disconnectAttribution?: VoiceCallDisconnectAttributionLike | null;
    readonly onSeek?: (seconds: number) => void;
    // Optional content rendered between the call summary and the transcript
    // (e.g. the recording player).
    readonly afterSummary?: ReactNode;
    // The call dialog already shows the summary in the thread card, so it
    // renders only the transcript here.
    readonly showSummary?: boolean;
}) {
    const disconnectAttributionLabel = getDisconnectAttributionLabel(
        disconnectAttribution,
    );
    const callEndedTimelineEvent =
        disconnectAttributionLabel && disconnectAttribution ? (
            <CallEndedTimelineEvent
                attribution={disconnectAttribution}
                label={disconnectAttributionLabel}
            />
        ) : null;
    const attemptedTransferTimelineEvent = viewModel.attemptedTransferLabel ? (
        <AttemptedTransferTimelineEvent
            destinationLabel={viewModel.attemptedTransferLabel}
        />
    ) : null;

    if (
        !viewModel.hasContent &&
        !callEndedTimelineEvent &&
        !attemptedTransferTimelineEvent
    ) {
        return (
            <p className="text-muted-foreground text-sm">
                No recording, summary, or transcript is available for this call
                yet.
            </p>
        );
    }

    return (
        <div className="space-y-6">
            {viewModel.hasContent && showSummary ? (
                <section className="space-y-2">
                    <SectionHeader Icon={Mic} label="Call summary" />
                    <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                        {viewModel.summaryText}
                    </p>
                </section>
            ) : null}
            {viewModel.hasContent ? afterSummary : null}
            {viewModel.transcriptRows.length > 0 ? (
                <section aria-label="Transcript" className="space-y-3">
                    <SectionHeader Icon={Hash} label="Transcript" />
                    <div className="space-y-4">
                        {viewModel.transcriptRows.map((turn) => {
                            const speakerDisplayLabel = getSpeakerDisplayLabel(
                                turn.speakerLabel,
                            );
                            const seconds = parseTimestampLabelToSeconds(
                                turn.timestampLabel,
                            );
                            const onTurnClick =
                                onSeek && seconds !== null
                                    ? () => onSeek(seconds)
                                    : undefined;
                            const rowInner = (
                                <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-3 text-sm leading-6">
                                    <span className="text-foreground text-xs font-bold whitespace-nowrap">
                                        {speakerDisplayLabel}
                                    </span>
                                    <div className="min-w-0 space-y-1">
                                        <p className="text-foreground break-words">
                                            {turn.text}
                                        </p>
                                        <TranscriptTurnTimestamp turn={turn} />
                                    </div>
                                </div>
                            );
                            return (
                                <Fragment key={turn.key}>
                                    {turn.isPostTransferStart ? (
                                        <div className="flex justify-center">
                                            <Badge
                                                variant="success"
                                                className="max-w-full px-2 py-0.5 text-[10px]"
                                            >
                                                <span className="truncate">
                                                    {viewModel.transferDestinationLabel ??
                                                        'Transferred to back office'}
                                                </span>
                                            </Badge>
                                        </div>
                                    ) : null}
                                    {onTurnClick ? (
                                        <button
                                            className="hover:bg-accent w-full rounded-md px-2 py-1 text-left transition-colors"
                                            onClick={onTurnClick}
                                            title="Jump to this moment in the recording"
                                            type="button"
                                        >
                                            {rowInner}
                                        </button>
                                    ) : (
                                        <div className="px-2 py-1">
                                            {rowInner}
                                        </div>
                                    )}
                                </Fragment>
                            );
                        })}
                        {attemptedTransferTimelineEvent}
                        {callEndedTimelineEvent}
                    </div>
                </section>
            ) : viewModel.transcriptText ? (
                <section aria-label="Transcript" className="space-y-3">
                    <SectionHeader Icon={Hash} label="Transcript" />
                    <div className="space-y-4">
                        <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                            {viewModel.transcriptText}
                        </p>
                        {attemptedTransferTimelineEvent}
                        {callEndedTimelineEvent}
                    </div>
                </section>
            ) : callEndedTimelineEvent || attemptedTransferTimelineEvent ? (
                <section aria-label="Transcript" className="space-y-3">
                    <SectionHeader Icon={Hash} label="Transcript" />
                    {attemptedTransferTimelineEvent}
                    {callEndedTimelineEvent}
                </section>
            ) : null}
            {viewModel.translation ? (
                <section className="space-y-2">
                    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                        {`Translated from ${viewModel.translation.source_language_name}`}
                    </p>
                    <p className="text-foreground text-sm leading-6 break-words whitespace-pre-wrap">
                        {viewModel.translation.translated_text}
                    </p>
                </section>
            ) : null}
        </div>
    );
}
